import { createTheme } from '@mui/material/styles';

// Common theme properties
const commonTheme = {
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontWeight: 700,
        },
        h2: {
            fontWeight: 600,
        },
        h3: {
            fontWeight: 600,
        },
        h4: {
            fontWeight: 600,
        },
        h5: {
            fontWeight: 500,
        },
        h6: {
            fontWeight: 500,
        },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: ({ theme, ownerState }) => ({
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: theme.palette.mode === 'light' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    '&:hover': {
                        boxShadow: theme.palette.mode === 'light' ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                    },
                }),
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundImage: 'none',
                    ...(theme.palette.mode === 'light' && {
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
                        border: `1px solid ${ theme.palette.grey[200] }`,
                    }),
                }),
                elevation1: ({ theme }) => ({
                    ...(theme.palette.mode === 'light' && {
                        backgroundColor: theme.palette.surface?.level1 || theme.palette.grey[50],
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    }),
                }),
                elevation2: ({ theme }) => ({
                    ...(theme.palette.mode === 'light' && {
                        backgroundColor: theme.palette.surface?.level2 || theme.palette.grey[100],
                        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                    }),
                }),
                elevation3: ({ theme }) => ({
                    ...(theme.palette.mode === 'light' && {
                        backgroundColor: theme.palette.surface?.level3 || theme.palette.grey[200],
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }),
                }),
            },
        },
        MuiCard: {
            styleOverrides: {
                root: ({ theme }) => ({
                    ...(theme.palette.mode === 'light' && {
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${ theme.palette.grey[200] }`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.1)',
                        '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1), 0 2px 6px rgba(0,0,0,0.15)',
                        },
                    }),
                }),
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: ({ theme }) => ({
                    ...(theme.palette.mode === 'light' && {
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.grey[50],
                            '&:hover': {
                                backgroundColor: theme.palette.background.paper,
                            },
                            '&.Mui-focused': {
                                backgroundColor: theme.palette.background.paper,
                            },
                            '& fieldset': {
                                borderColor: theme.palette.grey[300],
                            },
                            '&:hover fieldset': {
                                borderColor: theme.palette.grey[400],
                            },
                        },
                    }),
                }),
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: ({ theme }) => ({
                    ...(theme.palette.mode === 'light' && {
                        backgroundColor: theme.palette.grey[100],
                        '& .MuiTableCell-head': {
                            backgroundColor: 'transparent',
                            fontWeight: 600,
                            borderBottom: `2px solid ${ theme.palette.grey[300] }`,
                        },
                    }),
                }),
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: ({ theme }) => ({
                    // Light mode alternating rows
                    ...(theme.palette.mode === 'light' && {
                        '&:nth-of-type(even)': {
                            backgroundColor: theme.palette.grey[50], // Very light grey for even rows
                        },
                        '&:hover': {
                            backgroundColor: theme.palette.grey[100], // Slightly darker on hover
                        },
                    }),
                    // Dark mode alternating rows
                    ...(theme.palette.mode === 'dark' && {
                        '&:nth-of-type(even)': {
                            backgroundColor: 'rgba(255, 255, 255, 0.03)', // Subtle white tint for even rows
                        },
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.08)', // More visible on hover
                        },
                    }),
                }),
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: ({ theme }) => ({
                    ...(theme.palette.mode === 'light' && {
                        borderColor: theme.palette.grey[200],
                    }),
                }),
            },
        },
    },
};

// Light theme
export const lightTheme = createTheme({
    ...commonTheme,
    palette: {
        mode: 'light',
        primary: {
            main: '#FF9800', // Orange
            light: '#FFB74D',
            dark: '#F57C00',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#FFA726',
            light: '#FFCC02',
            dark: '#FB8C00',
            contrastText: '#ffffff',
        },
        background: {
            default: '#fafafa', // Very light grey background
            paper: '#ffffff',
        },
        surface: {
            level1: '#ffffff', // Pure white for subtle elevation
            level2: '#f8f8f8', // Very light grey for more pronounced elevation
            level3: '#f0f0f0', // Light grey for higher elevation
        },
        text: {
            primary: '#212121',
            secondary: '#616161',
            disabled: '#9e9e9e',
        },
        divider: '#e0e0e0',
        border: {
            light: '#f0f0f0',
            main: '#e0e0e0',
            dark: '#bdbdbd',
        },
        action: {
            active: '#FF9800',
            hover: 'rgba(255, 152, 0, 0.04)',
            selected: 'rgba(255, 152, 0, 0.08)',
            disabled: 'rgba(0, 0, 0, 0.26)',
            disabledBackground: 'rgba(0, 0, 0, 0.12)',
        },
        grey: {
            50: '#fafafa',
            100: '#f5f5f5',
            200: '#eeeeee',
            300: '#e0e0e0',
            400: '#bdbdbd',
            500: '#9e9e9e',
            600: '#757575',
            700: '#616161',
            800: '#424242',
            900: '#212121',
        },
        success: {
            main: '#4caf50',
            light: '#81c784',
            dark: '#388e3c',
        },
        error: {
            main: '#f44336',
            light: '#e57373',
            dark: '#d32f2f',
        },
        warning: {
            main: '#ff9800',
            light: '#ffb74d',
            dark: '#f57c00',
        },
        info: {
            main: '#2196f3',
            light: '#64b5f6',
            dark: '#1976d2',
        },
    },
});

// Dark theme
export const darkTheme = createTheme({
    ...commonTheme,
    palette: {
        mode: 'dark',
        primary: {
            main: '#FF9800', // Orange
            light: '#FFB74D',
            dark: '#F57C00',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#FFA726',
            light: '#FFCC02',
            dark: '#FB8C00',
            contrastText: '#ffffff',
        },
        background: {
            default: '#121212',
            paper: '#1e1e1e',
        },
        text: {
            primary: '#e0e0e0',
            secondary: '#b0b0b0',
        },
        divider: '#333333',
        action: {
            active: '#FFB74D',
            hover: 'rgba(255, 152, 0, 0.08)',
            selected: 'rgba(255, 152, 0, 0.16)',
            disabled: 'rgba(255, 255, 255, 0.3)',
        },
        success: {
            main: '#66bb6a',
        },
        error: {
            main: '#ef5350',
        },
        warning: {
            main: '#ffa726',
        },
        info: {
            main: '#42a5f5',
        },
    },
});

// Explorer-specific overrides
export const createExplorerTheme = (baseTheme) => createTheme({
    ...baseTheme,
    typography: {
        ...baseTheme.typography,
        h4: {
            ...baseTheme.typography.h4,
            color: baseTheme.palette.primary.main,
        },
        h5: {
            ...baseTheme.typography.h5,
            color: baseTheme.palette.primary.main,
        },
        h6: {
            ...baseTheme.typography.h6,
            color: baseTheme.palette.text.primary,
        },
    },
});
